import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Alert, Box, Button, Checkbox, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { MAIN_URL } from "../../Configurations/Urls";
import Layout4 from "../DataLayouts/Layout4";
import TableDataGeneric from "../../Configurations/TableDataGeneric";
import ListSummaryCards from "../../Components/List/ListSummaryCards";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function pairsToKeySet(pairs, objectKeys) {
  const s = new Set();
  (pairs || []).forEach((p) => {
    if (Array.isArray(p) && p.length >= 2) {
      s.add(`${p[0]}:${p[1]}`);
    } else if (p && typeof p === "object") {
      const a = p[objectKeys[0]];
      const b = p[objectKeys[1]];
      if (a != null && b != null) s.add(`${a}:${b}`);
    }
  });
  return s;
}

export default function UserRolesAccess() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [applications, setApplications] = useState([]);
  const [roles, setRoles] = useState([]);
  const [supportsRoleMatrix, setSupportsRoleMatrix] = useState(true);
  const [rolePairKeys, setRolePairKeys] = useState(() => new Set());

  const loadAssignment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${MAIN_URL}/api/core/user-role-assignment`, {
        headers: authHeaders(),
      });
      const d = res.data || {};
      setApplications(d.applications || []);
      setRoles(d.roles || []);
      setSupportsRoleMatrix(d.supports_role_matrix !== false);
      setRolePairKeys(
        pairsToKeySet(d.role_pairs, ["application_id", "system_user_role_id"])
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load role assignment data."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignment();
  }, [loadAssignment]);

  const pairKey = (appId, roleId) => `${appId}:${roleId}`;

  const buildPayloadFromKeys = (keys) => {
    const role_pairs = [];
    keys.forEach((key) => {
      const [application_id, system_user_role_id] = key.split(":").map(Number);
      role_pairs.push({ application_id, system_user_role_id });
    });
    return { role_pairs };
  };

  const persistMatrix = useCallback(
    async (keys, { reload = true } = {}) => {
      setSaving(true);
      try {
        await axios.put(
          `${MAIN_URL}/api/core/user-role-assignment`,
          buildPayloadFromKeys(keys),
          {
            headers: {
              ...authHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Saved to application roles.");
        if (reload) {
          await loadAssignment();
        }
      } catch (err) {
        const data = err.response?.data;
        const msg =
          (typeof data?.message === "string" && data.message) ||
          err.message ||
          "Save failed.";
        toast.error(msg);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [loadAssignment]
  );

  const handleSave = async () => {
    await persistMatrix(rolePairKeys);
  };

  const toggleRoleCell = useCallback((applicationId, systemUserRoleId) => {
    const key = pairKey(applicationId, systemUserRoleId);
    setRolePairKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const configss = useMemo(
    () => [
      {
        field: "user_role_name",
        label: "System role",
        visible: true,
        width: 260,
        filterable: true,
        sortable: true,
        pinned: "left",
        required: true,
      },
      ...applications.map((app) => ({
        field: `app_${app.application_id}`,
        label: app.application_name || `App ${app.application_id}`,
        visible: true,
        width: 140,
        filterable: false,
        sortable: false,
        pinned: "none",
        required: false,
      })),
    ],
    [applications]
  );

  const renderCell = useCallback(
    (row, col) => {
      if (col?.key === "user_role_name") {
        return (
          <Box>
            <Box sx={{ fontWeight: 700 }}>{row.user_role_name}</Box>
            {row.description ? (
              <Box sx={{ fontSize: 12, color: "var(--ems-muted)" }}>
                {row.description}
              </Box>
            ) : null}
          </Box>
        );
      }
      if (!String(col?.key || "").startsWith("app_")) return undefined;
      const appId = Number(String(col.key).replace("app_", ""));
      return (
        <Checkbox
          size="small"
          disabled={saving || loading || !supportsRoleMatrix}
          checked={rolePairKeys.has(pairKey(appId, row.system_user_role_id))}
          onChange={() => toggleRoleCell(appId, row.system_user_role_id)}
          inputProps={{ "aria-label": `${col.label} — ${row.user_role_name}` }}
        />
      );
    },
    [loading, rolePairKeys, saving, supportsRoleMatrix, toggleRoleCell]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "apps",
        label: "Applications",
        value: applications.length,
        iconKey: "apps",
      },
      {
        key: "roles",
        label: "System roles",
        value: roles.length,
        iconKey: "shield",
      },
      {
        key: "assigned",
        label: "Assigned pairs",
        value: rolePairKeys.size,
        iconKey: "check",
        tone: "ok",
      },
    ],
    [applications.length, roles.length, rolePairKeys]
  );

  const saveButton = (
    <Button
      className="ems-btn ems-primary"
      variant="contained"
      startIcon={
        saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
      }
      disabled={saving || loading || !supportsRoleMatrix}
      onClick={handleSave}
    >
      Save matrix
    </Button>
  );

  if (loading && applications.length === 0 && !error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && applications.length === 0) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Layout4
        loading={loading}
        heading="User roles access"
        subtitle="Rows are system user roles. Columns are applications — tick the cells you want, then save."
        showAddButton={false}
        headerExtra={saveButton}
      />
      {!supportsRoleMatrix && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Run database migrations to add{" "}
          <strong>application_user_roles.system_user_role_id</strong> before
          saving (php artisan migrate).
        </Alert>
      )}
      <ListSummaryCards cards={summaryCards} />
      {applications.length === 0 ? (
        <Alert severity="info">No applications defined.</Alert>
      ) : (
        <TableDataGeneric
          tableName="user_role_assignment"
          primaryKey="system_user_role_id"
          heading="User roles access"
          data={roles}
          sortname="user_role_name"
          showActions={false}
          token={localStorage.getItem("token")}
          configss={configss}
          renderCell={renderCell}
          enableRowSelection={false}
        />
      )}
    </>
  );
}
